import React, { useMemo } from 'react';
import './Sidebar.css';
import { Lang, t } from '../../../i18n';
import Conversation from './Conversation';
import { API_BASE_URL } from '../../../services/apiConfig';
import { AssignementRow } from '../../../services/sqlApi';
import { Conversation as ConversationModel } from '../Chat/Conversation';

interface SidebarProps {
  currentUser: string;
  onLogout: () => void;
  lang: Lang;
  assignements: AssignementRow[];
  conversations: ConversationModel[];
  setSelectedConversationId: (id: string) => void;
  selectedConversationId: string | null;
  isOpen: boolean;
  onClose: () => void;
  onRequestNewAssignement: () => void | Promise<void>;
  onCompleteAssignement: (assignementId: number) => void;
  isAssignementGroupExpanded: (a: AssignementRow) => boolean;
  onToggleAssignementGroup: (assignementId: number) => void;
}

const SettingsIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" style={{ verticalAlign: 'middle' }} xmlns="http://www.w3.org/2000/svg">
    <path d="M12 15.5A3.5 3.5 0 1 0 12 8.5a3.5 3.5 0 0 0 0 7zm7.43-2.9l-1.06-.18a6.97 6.97 0 0 0-.36-1.02l.6-.9a1 1 0 0 0-.13-1.28l-1.06-1.06a1 1 0 0 0-1.28-.13l-.9.6c-.33-.13-.67-.24-1.02-.36l-.18-1.06A1 1 0 0 0 13 4h-2a1 1 0 0 0-1 .84l-.18 1.06c-.35.12-.69.23-1.02.36l-.9-.6a1 1 0 0 0-1.28.13L4.84 7.47a1 1 0 0 0-.13 1.28l.6.9c-.13.33-.24.67-.36 1.02l-1.06.18A1 1 0 0 0 2 11v2c0 .5.36.92.84 1l1.06.18c.12.35.23.69.36 1.02l-.6.9a1 1 0 0 0 .13 1.28l1.06 1.06a1 1 0 0 0 1.28.13l.9-.6c.33.13.67.24 1.02.36l.18 1.06A1 1 0 0 0 11 20h2c.5 0 .92-.36 1-.84l.18-1.06c.35-.12.69-.23 1.02-.36l.9.6a1 1 0 0 0 1.28-.13l1.06-1.06a1 1 0 0 0 .13-1.28l-.6-.9c.13-.33.24-.67.36-1.02l1.06-.18A1 1 0 0 0 22 13v-2c0-.5-.36-.92-.84-1z" stroke="#a0a0a0" strokeWidth="2" fill="none"/>
  </svg>
);

const Sidebar: React.FC<SidebarProps> = ({
  currentUser,
  onLogout,
  lang,
  assignements,
  conversations,
  setSelectedConversationId,
  selectedConversationId,
  isOpen,
  onClose,
  onRequestNewAssignement,
  onCompleteAssignement,
  isAssignementGroupExpanded,
  onToggleAssignementGroup,
}) => {
  const today = new Date().toLocaleDateString(lang === 'en' ? 'en-US' : 'nl-NL', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const sortedAssignements = useMemo(
    () => [...assignements].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()),
    [assignements]
  );

  const sortedOpenAssignements = useMemo(
    () => sortedAssignements.filter((a) => !a.is_completed),
    [sortedAssignements]
  );

  const firstOpenAssignementId = sortedOpenAssignements[0]?.id ?? null;

  const unassignedConversations = useMemo(() => {
    if (sortedAssignements.length > 0) {
      return [];
    }
    return conversations.filter((c) => c.assignementId == null);
  }, [conversations, sortedAssignements.length]);

  const conversationsForAssignement = (a: AssignementRow) =>
    conversations.filter((c) => {
      if (c.assignementId === a.id) return true;
      if (
        firstOpenAssignementId != null &&
        a.id === firstOpenAssignementId &&
        c.assignementId == null
      ) {
        return true;
      }
      return false;
    });

  const handleSettingsClick = async () => {
    const submitted = conversations.filter((conv) => conv.isSubmitted);
    const invoiceRows: Array<Record<string, any>> = [];
    submitted.forEach((conv) => {
      conv.messages.forEach((msg: any) => {
        if (
          msg.messageType === 'assistant' &&
          msg.finalTaskId &&
          msg.billing &&
          msg.billing[msg.finalTaskId]
        ) {
          const billing = msg.billing[msg.finalTaskId];
          invoiceRows.push({
            'BESTEKS POST- NUMMER': msg.finalTaskId,
            OMSCHRIJVING: msg.taskDescription || '',
            'EEN-HEID': billing.billable_unit || '',
            'HOEVEELHEID RESULTAATS- VERPLICHTING': billing.quantity_result_obligation || '',
            'VERWERKTE HOEVEELHEID IN TERMIJN': msg.amount || '1',
            'PRIJS PER EENHEID': billing.price_per_unit_in_euro || '',
          });
        }
      });
    });

    try {
      const response = await fetch(`${API_BASE_URL}/generate-invoice`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(invoiceRows),
      });
      if (!response.ok) {
        alert(t(lang, 'failedToGenerateInvoice'));
        return;
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Invoice.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(t(lang, 'errorGeneratingInvoice'));
      console.error(err);
    }
  };

  const renderConversationList = (list: ConversationModel[]) => (
    <div className="sidebar-conversation-list">
      {list.map((conv) => (
        <Conversation
          key={conv.id}
          id={conv.id}
          time={conv.time}
          selected={conv.id === selectedConversationId}
          onClick={() => setSelectedConversationId(conv.id)}
        />
      ))}
    </div>
  );

  return (
    <div className={`sidebar ${isOpen ? 'open' : ''}`}>
      <button className="sidebar-close" onClick={onClose} aria-label={t(lang, 'closeSidebar')}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" fill="currentColor"/>
        </svg>
      </button>

      <div className="sidebar-header">
        <img
          src="/RIOREGIE_REMMONDIS-removebg-preview.png"
          alt="RioRegie"
          className="sidebar-logo"
        />
      </div>
      <div className="sidebar-content">
        <div className="user-info">
          <div className="user-section-title">
            {t(lang, 'employee')}
          </div>
          <div className="user-details">
            <span className="user-name">{currentUser}</span>
            <span className="user-date">{today}</span>
            <div className="status-info">
              <span className="status-indicator"></span>
              <span className="status-text">{t(lang, 'online')}</span>
            </div>
          </div>
        </div>
        <div className="tasks-section">
          <h3>{t(lang, 'todaysTasks')}</h3>
        </div>
        <div className="sidebar-section assignements-sidebar" style={{ marginTop: 16 }}>
          <div className="assignement-groups-scroll">
            {sortedAssignements.map((a, index) => {
              const groupConvs = conversationsForAssignement(a);
              const expanded = isAssignementGroupExpanded(a);
              const numberLabel = t(lang, 'assignementNumber', { n: index + 1 });
              return (
                <div key={a.id} className={`assignement-group ${a.is_completed ? 'completed' : ''}`}>
                  <button
                    type="button"
                    className="assignement-group-header"
                    onClick={() => onToggleAssignementGroup(a.id)}
                    aria-expanded={expanded}
                  >
                    <span className="assignement-chevron">{expanded ? '▼' : '▶'}</span>
                    <span className="assignement-header-text">
                      <span className="assignement-title">{numberLabel}</span>
                      <span className="assignement-address-line" title={a.address}>
                        {a.address}
                      </span>
                    </span>
                    {a.is_completed && (
                      <span className="assignement-badge">{t(lang, 'assignementCompleted')}</span>
                    )}
                  </button>
                  {!a.is_completed && (
                    <button
                      type="button"
                      className="assignement-complete-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        onCompleteAssignement(a.id);
                      }}
                    >
                      {t(lang, 'assignementMarkComplete')}
                    </button>
                  )}
                  {expanded && (
                    <div className="assignement-group-body">
                      <div className="assignement-chats-label">{t(lang, 'assignementChats')}</div>
                      {groupConvs.length === 0 ? (
                        <div className="assignement-empty-chats">—</div>
                      ) : (
                        renderConversationList(groupConvs)
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            {unassignedConversations.length > 0 && (
              <div className="assignement-group unassigned">
                <div className="assignement-group-header static">
                  <span className="assignement-header-text">
                    <span className="assignement-title">{t(lang, 'assignementNumber', { n: 1 })}</span>
                  </span>
                </div>
                <div className="assignement-group-body">
                  {renderConversationList(unassignedConversations)}
                </div>
              </div>
            )}
          </div>
          <div className="assignement-toolbar">
            <button type="button" className="assignement-toolbar-btn" onClick={onRequestNewAssignement}>
              {t(lang, 'assignementNew')}
            </button>
          </div>
        </div>
      </div>
      <div className="sidebar-footer">
        <button onClick={onLogout} className="logout-btn" title={t(lang, 'logout')}>
          {t(lang, 'logout')}
        </button>
        <button className="settings-btn" title={t(lang, 'settings')} onClick={handleSettingsClick}>
          <SettingsIcon />
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
